import { CSSObject } from "@emotion/react";
import { Link } from "react-router-dom";
import { Colors, Icon, useFeatureFlags } from "swing-components";

import { createMyDocumentsUrl } from "~pages";
import { msg } from "~utils";

export interface ExpiringDocument {
  requirement: string;
  name: string;
  description: string | null;
  status: "valid" | "indefinite" | "expired" | string;
  expiration: string | null;
  attrKw: string;
  newDocId: string | number | null;
  // Because the key contains a question mark, it must be wrapped in quotes.
  // Access this property using bracket notation: doc["mandatory?"]
  "mandatory?": boolean;
}

const MRT_DOCUMENT_NAME = "CA Mandated Reporter Acknowledgement";

export function ExpiringDocumentsBanner({ documents }: { documents: ExpiringDocument[] }) {
  const { flags } = useFeatureFlags();
  const isV2Enabled = flags.expiringDocumentBannerV2;
  const expiredMandatoryDocuments = documents.filter((doc) => doc["mandatory?"] === true);

  const hasOnlyExpiredMRTDocument =
    expiredMandatoryDocuments.length === 1 &&
    expiredMandatoryDocuments.at(0)?.name === MRT_DOCUMENT_NAME;

  const hasExpiredMandatoryDocuments =
    !hasOnlyExpiredMRTDocument && expiredMandatoryDocuments.length > 0;

  let bannerContent = null;

  if (!isV2Enabled) {
    bannerContent = multipleExpiredDocumentsBanner();
  } else if (hasOnlyExpiredMRTDocument) {
    bannerContent = expiringMRTDocumentBanner();
  } else if (hasExpiredMandatoryDocuments) {
    bannerContent = multipleExpiredDocumentsBanner();
  }

  return (
    <div
      role="status"
      css={
        {
          backgroundColor: Colors.red200,
          color: Colors.white200,
          display: "flex",
          gap: 8,
          paddingRight: 8,
          paddingBottom: 4,
          paddingLeft: 8,
          marginTop: 16,
          marginLeft: 16,
          marginRight: 16,
          border: `1px solid ${Colors.red500}`,
          borderRadius: 8,
          alignItems: "top",
          justifyContent: "left",
          // Display under AliasBanner if user is aliased
          // 29px comes from height + padding of AliasBanner

          "@media (min-width: 900px)": {
            padding: 8,
          },
        } as CSSObject
      }
    >
      {bannerContent}
    </div>
  );
}

interface BannerContentProps {
  title: string;
  description: React.ReactNode;
  hasMargin?: boolean;
}

function BannerContent({ title, description }: BannerContentProps) {
  return (
    <>
      <div css={{ marginTop: 8, marginLeft: 8 }}>
        <Icon name="Alert" color={Colors.red500} />
      </div>
      <div css={{ color: Colors.black, fontWeight: "var(--swing-font-semibold)", marginTop: 8 }}>
        {title}
        <div css={{ color: Colors.black, fontWeight: "var(--swing-font-normal)", marginTop: 8 }}>
          {description}
        </div>
      </div>
    </>
  );
}

// Only for MRT
function expiringMRTDocumentBanner() {
  const MRT_DOCUMENT_NAME = "Mandated Reporter Training";
  const MRT_LINK =
    "https://support.swingeducation.com/en/articles/11120208-how-to-complete-the-mandated-reporter-training";
  const description = [
    "Complete the ",
    <span key="doc-name">
      <a key="mrt-link" href={MRT_LINK}>
        {MRT_DOCUMENT_NAME}
      </a>
    </span>,
    " and upload in ",
    <Link key="my-docs-link" to={createMyDocumentsUrl()}>
      My Documents
    </Link>,
    " to resume accepting requests.",
  ];
  return (
    <BannerContent
      title={`${msg("EXPIRING_DOCUMENTS_ACC_PAUSED_LABEL_PREFIX")} Your ${MRT_DOCUMENT_NAME} is past due`}
      description={description}
    />
  );
}

function multipleExpiredDocumentsBanner() {
  const description = [
    "Update in ",
    <Link key="my-docs-link" to={createMyDocumentsUrl()}>
      My Documents.
    </Link>,
  ];
  return (
    <BannerContent
      title={`${msg("EXPIRING_DOCUMENTS_MULTIPLE_REQUIREMENTS_LABEL")}`}
      description={description}
    />
  );
}
